import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navigation from '../components/Navigation';
import AppleWatchWizard from '../components/AppleWatchWizard';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Heart, Activity, Moon, Apple, ExternalLink, CheckCircle, Info } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

const HealthData = ({ user, setUser }) => {
  const { t } = useTranslation();
  const [healthData, setHealthData] = useState([]);
  const [showWizard, setShowWizard] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [syncInstructions, setSyncInstructions] = useState(null);
  const [formData, setFormData] = useState({
    source: 'apple_watch',
    heart_rate: '',
    steps: '',
    sleep_hours: '',
    date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    loadHealthData();
  }, []);

  const loadHealthData = async () => {
    try {
      const response = await axios.get(`${API}/health-data`);
      setHealthData(response.data);
    } catch (error) {
      console.error('Error loading health data:', error);
    }
  };

  const handleSync = async () => {
    try {
      await axios.post(`${API}/health-data`, formData);
      toast.success('Health data synced successfully!');
      loadHealthData();
      setFormData({
        source: 'apple_watch',
        heart_rate: '',
        steps: '',
        sleep_hours: '',
        date: new Date().toISOString().split('T')[0]
      });
    } catch (error) {
      toast.error('Failed to sync health data');
    }
  };

  const getLatestData = () => {
    if (healthData.length === 0) return null;
    return healthData[0];
  };

  const latest = getLatestData();

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-teal-50">
      <Navigation user={user} setUser={setUser} />
      
      <div className="container mx-auto px-4 py-6 sm:py-8 pt-24 " data-testid="health-data-page">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-800 mb-2">{t('healthData.title')}</h1>
            <p className="text-gray-600 text-lg">{t('healthData.subtitle')}</p>
          </div>
          <Button
            onClick={() => setShowWizard(true)}
            className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-xl transition-all flex items-center gap-2"
            data-testid="apple-watch-setup-btn"
          >
            <Apple className="w-5 h-5" />
            {t('healthData.connectAppleWatch')}
          </Button>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          <Card className="p-6 bg-gradient-to-br from-red-500 to-pink-600 text-white shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <Heart className="w-10 h-10" />
              <span className="text-4xl font-bold">{latest?.heart_rate || '--'}</span>
            </div>
            <p className="text-sm opacity-90">{t('healthData.heartRateBPM')}</p>
            <p className="text-xs opacity-75 mt-1">{t('healthData.latestReading')}</p>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-green-500 to-teal-600 text-white shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <Activity className="w-10 h-10" />
              <span className="text-4xl font-bold">{latest?.steps || '--'}</span>
            </div>
            <p className="text-sm opacity-90">{t('healthData.stepsToday')}</p>
            <p className="text-xs opacity-75 mt-1">{t('healthData.keepMoving')}</p>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <Moon className="w-10 h-10" />
              <span className="text-4xl font-bold">{latest?.sleep_hours || '--'}</span>
            </div>
            <p className="text-sm opacity-90">{t('healthData.sleepHours')}</p>
            <p className="text-xs opacity-75 mt-1">{t('healthData.lastNight')}</p>
          </Card>
        </div>

        <Card className="p-8 bg-white shadow-xl mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">{t('healthData.syncNewData')}</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label>{t('healthData.source')}</Label>
              <select
                className="w-full px-3 py-2 border rounded-md mt-1"
                value={formData.source}
                onChange={(e) => setFormData({ ...formData, source: e.target.value })}
              >
                <option value="apple_watch">{t('healthData.appleWatch')}</option>
                <option value="fitbit">{t('healthData.fitbit')}</option>
                <option value="apple_health">{t('healthData.appleHealth')}</option>
              </select>
            </div>
            <div>
              <Label>{t('healthData.date')}</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>{t('healthData.heartRateBPM')}</Label>
              <Input
                type="number"
                value={formData.heart_rate}
                onChange={(e) => setFormData({ ...formData, heart_rate: e.target.value })}
                placeholder="72"
                className="mt-1"
              />
            </div>
            <div>
              <Label>{t('healthData.steps')}</Label>
              <Input
                type="number"
                value={formData.steps}
                onChange={(e) => setFormData({ ...formData, steps: e.target.value })}
                placeholder="10000"
                className="mt-1"
              />
            </div>
            <div>
              <Label>{t('healthData.sleepHoursLabel')}</Label>
              <Input
                type="number"
                step="0.1"
                value={formData.sleep_hours}
                onChange={(e) => setFormData({ ...formData, sleep_hours: e.target.value })}
                placeholder="7.5"
                className="mt-1"
              />
            </div>
          </div>
          <Button
            onClick={handleSync}
            className="mt-6 w-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-3"
            data-testid="sync-health-data-btn"
          >
            <Apple className="w-5 h-5 mr-2" />
            {t('healthData.syncHealthData')}
          </Button>
        </Card>

        <Card className="p-6 bg-white shadow-lg">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('healthData.history')}</h2>
          <div className="space-y-3">
            {healthData.map((data) => (
              <div key={data.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-semibold text-gray-800">{data.date}</p>
                  <p className="text-sm text-gray-500 capitalize">{data.source.replace('_', ' ')}</p>
                </div>
                <div className="flex gap-4 text-sm">
                  {data.heart_rate && (
                    <div className="flex items-center gap-1">
                      <Heart className="w-4 h-4 text-red-500" />
                      <span>{data.heart_rate} BPM</span>
                    </div>
                  )}
                  {data.steps && (
                    <div className="flex items-center gap-1">
                      <Activity className="w-4 h-4 text-green-500" />
                      <span>{data.steps} steps</span>
                    </div>
                  )}
                  {data.sleep_hours && (
                    <div className="flex items-center gap-1">
                      <Moon className="w-4 h-4 text-indigo-500" />
                      <span>{data.sleep_hours}h sleep</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Apple Watch Connection Wizard */}
      <AppleWatchWizard 
        user={user}
        isOpen={showWizard}
        onClose={() => setShowWizard(false)}
      />
    </div>
  );
};

export default HealthData;
